import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Calendar, ScanLine, History, Users, Building2, BookOpen,
  QrCode, Settings, LogOut, Menu, GraduationCap,
  ShieldCheck, BookMarked, ChevronRight,
  UserCheck, Sparkles, FileSpreadsheet, ClipboardList,
  ShieldAlert,
  FileUp,
  Trophy,
  ClipboardEdit, FileText,
  CalendarDays, Database, ListChecks, ArrowRightLeft,
  Megaphone, ChevronDown, Briefcase, Info, CheckCircle2, UserCircle, HelpCircle,
  Target, Award, DollarSign, LogIn, Loader2, Globe,
  HeartHandshake, ClipboardCheck, AlertTriangle, Home, School, FileBarChart,
  Stethoscope, Pill, ClipboardPlus, HeartPulse, Syringe,
  Package, DoorOpen, Trash, Handshake, CalendarClock, BookOpenCheck, Wrench, AlertOctagon, MapPin,
  MapPinned, UserRoundCog,
} from 'lucide-react';
import ViewContextDialog from './ViewContextDialog';
import { menuTatib } from '@/lib/aksesTatib';
import { tambahMenuUmum } from '@/lib/menuUmum';
import { menuPkg, perluMenuPkg, tambahMenuPkg } from '@/lib/menuPkg';
import { ambilKeanggotaan, ambilMenuPkg } from '@/lib/pkg';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { ROLE_LABELS, api } from '@/lib/api';
import { toast } from 'sonner';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import EnableNotifications from '@/components/pwa/EnableNotifications';
import { ChangePasswordDialog } from '@/components/security/ChangePasswordDialog';
import { OfflineQueueBadge } from '@/components/offline/OfflineQueueBadge';
import { CommandPalette } from '@/components/CommandPalette';
import { GURU_PENGGANTI_PATH, canManageGuruPengganti } from '@/lib/guruPengganti';
import { isEmbeddedApp, postToApp } from '@/lib/embed';

/**
 * Build sidebar items based ONLY on activeRole.
 * STRICT: tiap role hanya menampilkan menu yang relevan dengan peran tersebut.
 * Jika user punya banyak peran, dia harus switch role untuk akses menu peran lain.
 *
 * Returns either:
 * - Array of items (for roles without groups)
 * - Array of groups with {title, items} structure (for admin with groups)
 */
const SUBJECT_TEACHER_ROLES = ['guru', 'guru_ipa', 'guru_ips', 'guru_bahasa', 'guru_seni', 'guru_agama', 'guru_tik'];
const LAB_GROUP_BY_ROLE = {
  guru_ipa: { title: 'Menu Lab IPA', labKey: 'ipa', testidPrefix: 'lab-ipa' },
  guru_tik: { title: 'Menu Lab Komputer', labKey: 'komputer', testidPrefix: 'lab-komputer' },
  guru_bahasa: { title: 'Menu Lab Bahasa', labKey: 'bahasa', testidPrefix: 'lab-bahasa' },
  guru_agama: { title: 'Menu Lab Agama', labKey: 'agama', testidPrefix: 'lab-agama' },
  guru_ips: { title: 'Menu Lab IPS', labKey: 'ips', testidPrefix: 'lab-ips' },
  guru_seni: { title: 'Menu Lab Seni', labKey: 'seni', testidPrefix: 'lab-seni' },
};

function buildLabGroupItems(labKey, testidPrefix) {
  return [
    { to: `/lab/${labKey}/alat-bahan`, label: 'Alat dan Bahan Lab', icon: Package, testid: `nav-${testidPrefix}-alat-bahan` },
    { to: `/lab/${labKey}/jadwal`, label: 'Jadwal Penggunaan Lab', icon: CalendarClock, testid: `nav-${testidPrefix}-jadwal` },
    { to: `/lab/${labKey}/jurnal-penggunaan`, label: 'Jurnal Penggunaan Lab', icon: BookOpenCheck, testid: `nav-${testidPrefix}-jurnal-penggunaan` },
    { to: `/lab/${labKey}/jurnal-pengelolaan`, label: 'Jurnal Pengelolaan', icon: ClipboardList, testid: `nav-${testidPrefix}-jurnal-pengelolaan` },
    { to: `/lab/${labKey}/peminjaman-alat`, label: 'Peminjaman Alat', icon: Handshake, testid: `nav-${testidPrefix}-peminjaman-alat` },
    { to: `/lab/${labKey}/kerusakan`, label: 'Laporan Kerusakan', icon: AlertOctagon, testid: `nav-${testidPrefix}-kerusakan` },
  ];
}

// Menu Guru Pengganti hanya untuk admin, waka kurikulum, dan guru piket —
// disaring di sini agar guru mata pelajaran tidak pernah melihatnya.
function withoutGuruPengganti(entries) {
  return entries
    .filter((e) => e.to !== GURU_PENGGANTI_PATH)
    .map((e) => (e.items ? { ...e, items: e.items.filter((i) => i.to !== GURU_PENGGANTI_PATH) } : e));
}

function navForRoleDasar(role, userRoles = []) {
  const isSubjectTeacher = SUBJECT_TEACHER_ROLES.includes(role);
  const items = isSubjectTeacher
    ? [{ to: '/jurnal/scan', label: 'Jurnal Presisi', icon: ScanLine, testid: 'nav-scan', highlight: true }]
    : [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' }];

  const labGroup = LAB_GROUP_BY_ROLE[role];

  if (isSubjectTeacher) {
    items.push({ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' });
    items.push({ to: '/profile/guru', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-guru' });
    items.push({ to: '/jadwal', label: 'Jadwal Saya', icon: Calendar, testid: 'nav-jadwal' });
    items.push({ to: '/jadwal/atur', label: 'Atur Jadwal Saya', icon: ClipboardEdit, testid: 'nav-my-schedule' });
    items.push({ to: '/admin/jadwal-piket', label: 'Jadwal Piket', icon: ShieldAlert, testid: 'nav-piket-guru' });
    items.push({ to: '/my-agenda', label: 'Agenda Saya', icon: CalendarDays, testid: 'nav-my-agenda' });
    items.push({ to: '/jurnal/riwayat', label: 'Riwayat Jurnal', icon: History, testid: 'nav-jurnal-history' });
    items.push({ to: '/guru/indikator-materi', label: 'Input Indikator & Materi', icon: BookOpen, testid: 'nav-guru-indikator-materi' });
    items.push({ to: '/guru/kebersihan', label: 'Kebersihan Kelas', icon: Sparkles, testid: 'nav-guru-kebersihan' });
    items.push({ to: '/guru/laporan', label: 'Laporan', icon: FileText, testid: 'nav-guru-laporan' });
    items.push({ to: '/piket/tugas', label: 'Titipkan Tugas', icon: FileText, testid: 'nav-titipan-tugas' });
    items.push({ to: '/nilai/input', label: 'Input Nilai', icon: ClipboardEdit, testid: 'nav-grades-input' });
    items.push({ to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-guru' });
    items.push({ to: '/verval/ajuan-saya', label: 'Ajuan Verval Saya', icon: CheckCircle2, testid: 'nav-verval-guru' });
    items.push({ to: '/guru/materi', label: 'Materi Mapel', icon: BookOpen, testid: 'nav-guru-materi' });
    items.push({ to: '/guru/tugas', label: 'Tugas', icon: ClipboardList, testid: 'nav-guru-tugas' });
    items.push({ to: '/admin/gtk/e-kinerja', label: 'E-Kinerja', icon: Target, testid: 'nav-guru-ekinerja' });
    items.push({ to: '/admin/gtk/profesionalitas', label: 'Profesionalitas GTK', icon: Award, testid: 'nav-guru-profesionalitas' });
    items.push({ to: '/gtk/absensi-saya', label: 'Laporan Absensi Saya', icon: UserCheck, testid: 'nav-guru-absensi-saya' });

    if (labGroup) {
      return [
        { to: '/jurnal/scan', label: 'Jurnal Presisi', icon: ScanLine, testid: 'nav-scan', highlight: true },
        {
          title: 'Menu Guru',
          items: items.slice(1), // drop the highlighted Jurnal Presisi entry, already shown above
        },
        {
          title: labGroup.title,
          items: buildLabGroupItems(labGroup.labKey, labGroup.testidPrefix),
        },
      ];
    }

    // Plain "guru mata pelajaran" (no lab group): group the otherwise-flat
    // menu into logical sections instead of one long ungrouped list.
    if (role === 'guru') {
      return [
        { to: '/jurnal/scan', label: 'Jurnal Presisi', icon: ScanLine, testid: 'nav-scan', highlight: true },
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
        { to: '/profile/guru', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-guru' },
        { to: '/my-agenda', label: 'Agenda Saya', icon: CalendarDays, testid: 'nav-my-agenda' },
        {
          title: 'Jadwal & Jurnal',
          items: [
            { to: '/jadwal', label: 'Jadwal Saya', icon: Calendar, testid: 'nav-jadwal' },
            { to: '/jadwal/atur', label: 'Atur Jadwal Saya', icon: ClipboardEdit, testid: 'nav-my-schedule' },
            { to: '/admin/jadwal-piket', label: 'Jadwal Piket', icon: ShieldAlert, testid: 'nav-piket-guru' },
            { to: '/jurnal/riwayat', label: 'Riwayat Jurnal', icon: History, testid: 'nav-jurnal-history' },
            { to: '/piket/tugas', label: 'Titipkan Tugas', icon: FileText, testid: 'nav-titipan-tugas' },
          ],
        },
        {
          title: 'Akademik Kelas',
          items: [
            { to: '/guru/indikator-materi', label: 'Input Indikator & Materi', icon: BookOpen, testid: 'nav-guru-indikator-materi' },
            { to: '/guru/materi', label: 'Materi Mapel', icon: BookOpen, testid: 'nav-guru-materi' },
            { to: '/guru/tugas', label: 'Tugas', icon: ClipboardList, testid: 'nav-guru-tugas' },
            { to: '/nilai/input', label: 'Input Nilai', icon: ClipboardEdit, testid: 'nav-grades-input' },
            { to: '/guru/kebersihan', label: 'Kebersihan Kelas', icon: Sparkles, testid: 'nav-guru-kebersihan' },
            { to: '/guru/laporan', label: 'Laporan', icon: FileText, testid: 'nav-guru-laporan' },
            { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-guru' },
          ],
        },
        {
          title: 'Kepegawaian',
          items: [
            { to: '/admin/gtk/e-kinerja', label: 'E-Kinerja', icon: Target, testid: 'nav-guru-ekinerja' },
            { to: '/admin/gtk/profesionalitas', label: 'Profesionalitas GTK', icon: Award, testid: 'nav-guru-profesionalitas' },
            { to: '/gtk/absensi-saya', label: 'Laporan Absensi Saya', icon: UserCheck, testid: 'nav-guru-absensi-saya' },
            { to: '/verval/ajuan-saya', label: 'Ajuan Verval Saya', icon: CheckCircle2, testid: 'nav-verval-guru' },
          ],
        },
      ];
    }
  } else if (role === 'wali_kelas') {
    // Wali Kelas uses grouped menu structure: ringkasan kelas terpisah dari jadwal/administrasi.
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/wali-kelas', label: 'Dashboard Kelas', icon: BookMarked, testid: 'nav-wali-kelas' },
      {
        title: 'Data Kelas',
        items: [
          { to: '/wali-kelas/siswa', label: 'Data Siswa', icon: Users, testid: 'nav-wk-siswa' },
          { to: '/wali-kelas/jurnal-kelas', label: 'Jurnal Kelas', icon: ClipboardList, testid: 'nav-wk-jurnal' },
          { to: '/wali-kelas/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-wk-kehadiran' },
          { to: '/wali-kelas/kebersihan', label: 'Kebersihan Kelas', icon: Sparkles, testid: 'nav-wk-kebersihan' },
          { to: '/wali-kelas/laporan', label: 'Data Laporan', icon: FileText, testid: 'nav-wk-laporan' },
          ...menuTatib('wali_kelas', 'nav-wk'),
          { to: '/rapor', label: 'E-Rapor Kelas', icon: FileText, testid: 'nav-rapor-wk' },
        ],
      },
      {
        title: 'Jadwal & Administrasi',
        items: [
          { to: '/jadwal', label: 'Jadwal Kelas', icon: Calendar, testid: 'nav-wk-jadwal' },
          { to: '/jadwal/atur', label: 'Atur Jadwal Kelas', icon: ClipboardEdit, testid: 'nav-wk-my-schedule' },
          { to: '/admin/verval-siswa', label: 'Verval Data Siswa', icon: CheckCircle2, testid: 'nav-wk-verval-siswa' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-wk' },
        ],
      },
    ];
  } else if (role === 'guru_piket') {
    items.push({ to: '/piket/tugas', label: 'Tugas Hari Ini', icon: ListChecks, testid: 'nav-piket-tasks', highlight: true });
    items.push({ to: '/admin/jadwal-piket', label: 'Jadwal Piket', icon: ShieldAlert, testid: 'nav-piket' });
    items.push({ to: '/guru-pengganti', label: 'Guru Pengganti', icon: UserRoundCog, testid: 'nav-piket-guru-pengganti' });
    items.push({ to: '/jurnal/riwayat', label: 'Riwayat Jurnal Piket', icon: History, testid: 'nav-jurnal-history-piket' });
    items.push({ to: '/guru/kebersihan', label: 'Kebersihan Kelas', icon: Sparkles, testid: 'nav-piket-kebersihan' });
  } else if (role === 'guru_bk') {
    // Guru BK uses grouped menu structure, mirroring admin's Tatib & BK groups
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      {
        title: 'Umum',
        items: [
          { to: '/admin/siswa', label: 'Data Siswa', icon: GraduationCap, testid: 'nav-bk-siswa' },
          { to: '/admin/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-bk-kehadiran' },
          { to: '/admin/kebersihan', label: 'Kebersihan Kelas', icon: Sparkles, testid: 'nav-bk-kebersihan' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-bk' },
        ],
      },
      {
        title: 'Manajemen Tatib & BK',
        items: [
          ...menuTatib('guru_bk', 'nav-bk'),
        ],
      },
      {
        title: 'Menu BK',
        items: [
          { to: '/admin/bk/kunjungan', label: 'Kunjungan Konseling', icon: HeartHandshake, testid: 'nav-bk-kunjungan' },
          { to: '/admin/bk/clkb', label: 'Cek List Kebiasaan Belajar (CLKB)', icon: ClipboardCheck, testid: 'nav-bk-clkb' },
          { to: '/admin/bk/pcl', label: 'Problem Cek List (PCL)', icon: AlertTriangle, testid: 'nav-bk-pcl' },
          { to: '/admin/bk/home-visit', label: 'Jurnal Home Visit', icon: Home, testid: 'nav-bk-home-visit' },
          { to: '/admin/alumni', label: 'Data Alumni', icon: GraduationCap, testid: 'nav-bk-alumni' },
          { to: '/admin/bk/sekolah-lanjutan', label: 'Data Sekolah Lanjutan', icon: School, testid: 'nav-bk-sekolah-lanjutan' },
          { to: '/admin/bk/laporan', label: 'Laporan BK', icon: FileBarChart, testid: 'nav-bk-laporan-clkb-pcl' },
        ],
      },
    ];
  } else if (role === 'guru_tata_tertib') {
    // Guru Tata Tertib uses grouped menu structure, mirroring admin's Tatib & BK groups
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      {
        title: 'Umum',
        items: [
          { to: '/admin/siswa', label: 'Data Siswa', icon: GraduationCap, testid: 'nav-tatib-siswa' },
          { to: '/admin/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-tatib-kehadiran' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-tatib' },
        ],
      },
      {
        title: 'Manajemen Tatib & BK',
        items: [
          ...menuTatib('guru_tata_tertib', 'nav-tatib'),
        ],
      },
    ];
  } else if (role === 'guru_ekstrakurikuler') {
    items.push({ to: '/ekstrakurikuler', label: 'Ekstrakurikuler Saya', icon: Sparkles, testid: 'nav-ekstra-coach' });
    items.push({ to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-ekskul' });
  } else if (role === 'tenaga_kependidikan') {
    // Tenaga Kependidikan uses grouped menu structure: ringkasan pribadi terpisah dari kepegawaian.
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/profile/tendik', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-tendik' },
      { to: '/my-agenda', label: 'Agenda Saya', icon: CalendarDays, testid: 'nav-my-agenda-tendik' },
      { to: '/admin/jadwal-piket', label: 'Jadwal Piket', icon: ShieldAlert, testid: 'nav-piket-tendik' },
      {
        title: 'Kepegawaian',
        items: [
          { to: '/admin/gtk/e-kinerja', label: 'E-Kinerja', icon: Target, testid: 'nav-tendik-ekinerja' },
          { to: '/admin/gtk/profesionalitas', label: 'Profesionalitas GTK', icon: Award, testid: 'nav-tendik-profesionalitas' },
          { to: '/gtk/absensi-saya', label: 'Laporan Absensi Saya', icon: UserCheck, testid: 'nav-tendik-absensi-saya' },
          { to: '/verval/ajuan-saya', label: 'Ajuan Verval Saya', icon: CheckCircle2, testid: 'nav-verval-tendik' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-tendik' },
        ],
      },
    ];
  } else if (role === 'kepala_tata_usaha') {
    // Kepala Tata Usaha: grup Manajemen GTK yang sama seperti admin + Tata Tertib (lihat saja).
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/profile/tendik', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-ktu' },
      { to: '/my-agenda', label: 'Agenda Saya', icon: CalendarDays, testid: 'nav-my-agenda-ktu' },
      {
        title: 'Manajemen GTK',
        items: [
          { to: '/admin/gtk', label: 'Data GTK', icon: Briefcase, testid: 'nav-ktu-gtk' },
          { to: '/admin/buku-induk-kepegawaian', label: 'Buku Induk Kepegawaian', icon: BookMarked, testid: 'nav-ktu-buku-induk-kepegawaian' },
          { to: '/admin/gtk/laporan-absensi', label: 'Laporan Absensi', icon: UserCheck, testid: 'nav-ktu-absensi' },
          { to: '/gtk/absensi-saya', label: 'Laporan Absensi Saya', icon: UserCheck, testid: 'nav-ktu-absensi-saya' },
          { to: '/admin/gtk/agenda-guru', label: 'Agenda Guru', icon: Calendar, testid: 'nav-ktu-agenda-guru' },
          { to: '/admin/gtk/agenda-tendik', label: 'Agenda Tendik', icon: Calendar, testid: 'nav-ktu-agenda-tendik' },
          { to: '/admin/gtk/e-kinerja', label: 'E-Kinerja', icon: Target, testid: 'nav-ktu-ekinerja' },
          { to: '/admin/gtk/profesionalitas', label: 'Profesionalitas GTK', icon: Award, testid: 'nav-ktu-profesionalitas' },
          { to: '/admin/dana-rkam', label: 'DANA RKAM', icon: DollarSign, testid: 'nav-ktu-rkam' },
        ],
      },
      {
        title: 'Tata Tertib',
        items: menuTatib('kepala_tata_usaha', 'nav-ktu'),
      },
    ];
  } else if (role === 'siswa') {
    items.push({ to: '/profile/siswa', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-siswa' });
    items.push({ to: '/jadwal', label: 'Jadwal Saya', icon: Calendar, testid: 'nav-jadwal' });
    items.push({ to: '/siswa/materi', label: 'Materi Mapel', icon: BookOpen, testid: 'nav-siswa-materi' });
    items.push({ to: '/siswa/tugas', label: 'Tugas', icon: ClipboardList, testid: 'nav-siswa-tugas' });
    items.push({ to: '/siswa/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-siswa-kehadiran' });
    items.push(...menuTatib('siswa', 'nav-siswa'));
    items.push({ to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-siswa' });
    items.push({ to: '/ekstrakurikuler', label: 'Ekstrakurikuler', icon: Sparkles, testid: 'nav-ekstra-siswa' });
    items.push({ to: '/rapor', label: 'Rapor Saya', icon: FileText, testid: 'nav-rapor-siswa' });
    items.push({ to: '/verval/ajuan-saya', label: 'Ajuan Verval Saya', icon: CheckCircle2, testid: 'nav-verval-siswa' });
    items.push({ to: '/siswa/clkb', label: 'CLKB', icon: ClipboardCheck, testid: 'nav-siswa-clkb' });
    items.push({ to: '/siswa/pcl', label: 'PCL', icon: AlertTriangle, testid: 'nav-siswa-pcl' });
  } else if (role === 'kelas') {
    items.push({ to: '/kelas/siswa', label: 'Data Siswa', icon: Users, testid: 'nav-kelas-siswa' });
    items.push({ to: '/kelas/jadwal', label: 'Jadwal Kelas', icon: Calendar, testid: 'nav-kelas-jadwal' });
    items.push({ to: '/kelas/materi', label: 'Materi Mapel', icon: BookOpen, testid: 'nav-kelas-materi' });
    items.push({ to: '/kelas/tugas', label: 'Tugas', icon: ClipboardList, testid: 'nav-kelas-tugas' });
    items.push({ to: '/kelas/jurnal', label: 'Riwayat Jurnal Kelas', icon: History, testid: 'nav-kelas-jurnal' });
    items.push({ to: '/kelas/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-kelas-kehadiran' });
  } else if (role === 'orang_tua') {
    items.push({ to: '/dashboard', label: 'Anak Saya', icon: Users, testid: 'nav-ortu-anak' });
  } else if (role === 'waka_humas') {
    items.push({ to: '/admin/kegiatan-madrasah', label: 'Kegiatan Madrasah', icon: CalendarDays, testid: 'nav-waka-humas-kegiatan' });
    items.push({ to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-waka-humas' });
    items.push({ to: '/admin/pengumuman', label: 'Pengumuman', icon: Megaphone, testid: 'nav-waka-humas-announcements' });
    items.push({ to: '/admin/aplikasi-madrasah', label: 'Aplikasi Madrasah', icon: Globe, testid: 'nav-waka-humas-apps' });
  } else if (role === 'bendahara') {
    items.push({ to: '/admin/dana-rkam', label: 'DANA RKAM', icon: DollarSign, testid: 'nav-bendahara-rkam' });
    items.push({ to: '/admin/pip/penerima', label: 'Data Penerima PIP', icon: DollarSign, testid: 'nav-bendahara-pip-penerima' });
    items.push({ to: '/admin/pip/ajuan', label: 'Daftar Ajuan PIP', icon: FileText, testid: 'nav-bendahara-pip-ajuan' });
  } else if (role === 'kepala_sekolah') {
    // Kepala Sekolah uses grouped menu structure, mirroring admin's group layout (view-only).
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/profile/guru', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-kepsek' },
      { to: '/my-agenda', label: 'Agenda Saya', icon: CalendarDays, testid: 'nav-kepsek-agenda' },
      {
        title: 'Manajemen Siswa & GTK',
        items: [
          { to: '/admin/siswa', label: 'Data Siswa', icon: GraduationCap, testid: 'nav-kepsek-siswa' },
          { to: '/admin/gtk', label: 'Data GTK', icon: Briefcase, testid: 'nav-kepsek-gtk' },
          { to: '/admin/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-kepsek-kehadiran' },
          { to: '/admin/alumni', label: 'Data Alumni', icon: GraduationCap, testid: 'nav-kepsek-alumni' },
        ],
      },
      {
        title: 'Kepegawaian',
        items: [
          { to: '/admin/gtk/agenda-guru', label: 'Agenda Guru', icon: Calendar, testid: 'nav-kepsek-agenda-guru' },
          { to: '/admin/gtk/agenda-tendik', label: 'Agenda Tendik', icon: Calendar, testid: 'nav-kepsek-agenda-tendik' },
          { to: '/admin/gtk/e-kinerja', label: 'E-Kinerja', icon: Target, testid: 'nav-kepsek-ekinerja' },
          { to: '/admin/gtk/profesionalitas', label: 'Profesionalitas GTK', icon: Award, testid: 'nav-kepsek-profesionalitas' },
          { to: '/admin/gtk/laporan-absensi', label: 'Laporan Absensi GTK', icon: UserCheck, testid: 'nav-kepsek-laporan-absensi-gtk' },
        ],
      },
      {
        title: 'Akademik & Aktivitas',
        items: [
          { to: '/admin/jurnal', label: 'Data Jurnal', icon: ClipboardList, testid: 'nav-kepsek-jurnal' },
          { to: '/admin/kegiatan-madrasah', label: 'Kegiatan Madrasah', icon: CalendarDays, testid: 'nav-kepsek-kegiatan' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-kepsek' },
          { to: '/admin/kebersihan', label: 'Rekapitulasi Kebersihan', icon: Sparkles, testid: 'nav-kepsek-kebersihan' },
          { to: '/admin/dana-rkam', label: 'DANA RKAM', icon: DollarSign, testid: 'nav-kepsek-rkam' },
        ],
      },
      {
        title: 'Tatib & BK',
        items: [
          ...menuTatib('kepala_sekolah', 'nav-kepsek'),
          { to: '/admin/bk/kunjungan', label: 'Kunjungan Konseling', icon: HeartHandshake, testid: 'nav-kepsek-bk-kunjungan' },
          { to: '/admin/bk/laporan', label: 'Laporan BK', icon: FileBarChart, testid: 'nav-kepsek-bk-laporan' },
          { to: '/admin/bk/sekolah-lanjutan', label: 'Data Sekolah Lanjutan', icon: School, testid: 'nav-kepsek-sekolah-lanjutan' },
        ],
      },
      {
        title: 'Menu UKS',
        items: [
          { to: '/admin/uks/kunjungan', label: 'Data Kunjungan UKS', icon: Stethoscope, testid: 'nav-kepsek-uks-kunjungan' },
          { to: '/admin/uks/laporan', label: 'Laporan UKS', icon: FileBarChart, testid: 'nav-kepsek-uks-laporan' },
          { to: '/admin/uks/laporan-baru', label: 'Laporan UKS Baru', icon: FileBarChart, testid: 'nav-kepsek-uks-laporan-baru' },
        ],
      },
      {
        title: 'Menu Perpus',
        items: [
          { to: '/admin/perpus/kunjungan', label: 'Data Kunjungan Perpus', icon: Users, testid: 'nav-kepsek-perpus-kunjungan' },
          { to: '/admin/perpus/peminjaman', label: 'Data Peminjaman Perpus', icon: BookOpen, testid: 'nav-kepsek-perpus-peminjaman' },
          { to: '/admin/perpus/laporan', label: 'Laporan Perpus', icon: FileBarChart, testid: 'nav-kepsek-perpus-laporan' },
        ],
      },
      {
        title: 'Menu Sarpras',
        items: [
          { to: '/admin/sarpras/kerusakan', label: 'Laporan Kerusakan & Perbaikan', icon: AlertOctagon, testid: 'nav-kepsek-sarpras-kerusakan' },
        ],
      },
    ];
  } else if (role === 'unit_pelayanan') {
    items.push({ to: '/admin/kegiatan-madrasah', label: 'Kegiatan Madrasah', icon: CalendarDays, testid: 'nav-unitpel-kegiatan' });
    items.push({ to: '/admin/gtk/agenda-guru', label: 'Agenda Guru', icon: Calendar, testid: 'nav-unitpel-agenda-guru' });
    items.push({ to: '/admin/gtk/agenda-tendik', label: 'Agenda Tendik', icon: Calendar, testid: 'nav-unitpel-agenda-tendik' });
    items.push({ to: '/admin/siswa', label: 'Data Siswa', icon: GraduationCap, testid: 'nav-unitpel-siswa' });
    items.push({ to: '/unit-pelayanan/kelas', label: 'Kelas', icon: BookOpen, testid: 'nav-unitpel-kelas' });
    items.push({ to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-unitpel' });
  } else if (role === 'unit_kesehatan') {
    // Unit Kesehatan (petugas UKS) uses grouped menu structure, mirroring admin's Menu UKS group
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      {
        title: 'Menu UKS',
        items: [
          { to: '/admin/uks/kunjungan', label: 'Data Kunjungan', icon: Stethoscope, testid: 'nav-uks-kunjungan' },
          { to: '/admin/uks/ckg', label: 'Data CKG', icon: Syringe, testid: 'nav-uks-ckg' },
          { to: '/admin/uks/obat', label: 'Data Obat', icon: Pill, testid: 'nav-uks-obat' },
          { to: '/admin/uks/bmhp', label: 'Data BMHP', icon: Package, testid: 'nav-uks-bmhp' },
          { to: '/admin/uks/jenis-penanganan', label: 'Jenis Penanganan', icon: ClipboardPlus, testid: 'nav-uks-jenis-penanganan' },
          { to: '/admin/uks/penegakan-diagnosa', label: 'Penegakan Diagnosa', icon: ClipboardCheck, testid: 'nav-uks-diagnosa' },
          { to: '/admin/uks/data-siswa-gtk', label: 'Data Siswa dan GTK', icon: Users, testid: 'nav-uks-data-siswa-gtk' },
          { to: '/admin/uks/aset', label: 'Aset UKS', icon: HeartPulse, testid: 'nav-uks-aset' },
          { to: '/admin/uks/laporan', label: 'Laporan UKS', icon: FileBarChart, testid: 'nav-uks-laporan' },
          { to: '/admin/uks/laporan-baru', label: 'Laporan UKS Baru', icon: FileBarChart, testid: 'nav-uks-laporan-baru' },
        ],
      },
    ];
  } else if (role === 'admin') {
    // Admin uses grouped menu structure
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      {
        title: 'Master App',
        items: [
          { to: '/admin/tahun-takwim', label: 'Tahun Takwim', icon: Calendar, testid: 'nav-tahun-takwim' },
          { to: '/admin/academic-year', label: 'Tahun Pelajaran', icon: GraduationCap, testid: 'nav-academic-year' },
          { to: '/admin/semesters', label: 'Semester', icon: CalendarDays, testid: 'nav-semesters' },
          { to: '/admin/kurikulum', label: 'Kurikulum', icon: BookOpen, testid: 'nav-curriculums' },
          { to: '/admin/subjects', label: 'Mata Pelajaran', icon: BookMarked, testid: 'nav-subjects' },
          { to: '/admin/rooms', label: 'Ruangan', icon: Building2, testid: 'nav-rooms' },
          { to: '/admin/classes', label: 'Kelas', icon: BookOpen, testid: 'nav-classes' },
          { to: '/admin/jabatan', label: 'Jabatan', icon: Briefcase, testid: 'nav-jabatan' },
          { to: '/admin/holidays', label: 'Hari Libur', icon: CalendarDays, testid: 'nav-admin-holidays' },
          { to: '/admin/qr-generator', label: 'QR Generator', icon: QrCode, testid: 'nav-qr' },
        ],
      },
      {
        title: 'Manajemen Pengguna',
        items: [
          { to: '/admin/users', label: 'Pengguna (GTK & Staff)', icon: Users, testid: 'nav-users' },
          { to: '/admin/pengguna-siswa', label: 'Pengguna Siswa', icon: UserCircle, testid: 'nav-pengguna-siswa' },
          { to: '/admin/mutasi', label: 'Data Mutasi', icon: ArrowRightLeft, testid: 'nav-admin-mutasi' },
          { to: '/admin/verval-siswa', label: 'Verval Data Siswa', icon: CheckCircle2, testid: 'nav-admin-verval-siswa' },
          { to: '/admin/verval-gtk', label: 'Verval Data GTK', icon: CheckCircle2, testid: 'nav-admin-verval-gtk' },
        ],
      },
      {
        title: 'Manajemen GTK',
        items: [
          { to: '/admin/gtk', label: 'Data GTK', icon: Briefcase, testid: 'nav-gtk' },
          { to: '/admin/buku-induk-kepegawaian', label: 'Buku Induk Kepegawaian', icon: BookMarked, testid: 'nav-admin-buku-induk-kepegawaian' },
          { to: '/admin/gtk/laporan-absensi', label: 'Laporan Absensi', icon: UserCheck, testid: 'nav-gtk-absensi' },
          { to: '/admin/gtk/agenda-guru', label: 'Agenda Guru', icon: Calendar, testid: 'nav-gtk-agenda-guru' },
          { to: '/admin/gtk/agenda-tendik', label: 'Agenda Tendik', icon: Calendar, testid: 'nav-gtk-agenda-tendik' },
          { to: '/admin/gtk/e-kinerja', label: 'E-Kinerja', icon: Target, testid: 'nav-gtk-ekinerja' },
          { to: '/admin/gtk/profesionalitas', label: 'Profesionalitas GTK', icon: Award, testid: 'nav-gtk-profesionalitas' },
        ],
      },
      {
        title: 'Manajemen Siswa',
        items: [
          { to: '/admin/siswa', label: 'Data Siswa', icon: GraduationCap, testid: 'nav-admin-siswa' },
          { to: '/admin/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-admin-kehadiran' },
          { to: '/admin/buku-induk', label: 'Buku Induk Siswa', icon: FileText, testid: 'nav-admin-buku-induk' },
          { to: '/admin/cetak-absensi-manual', label: 'Cetak Format Absensi Siswa Manual', icon: FileText, testid: 'nav-admin-cetak-absensi-manual' },
          { to: '/admin/pip/penerima', label: 'Data Penerima PIP', icon: DollarSign, testid: 'nav-admin-pip-penerima' },
          { to: '/admin/pip/ajuan', label: 'Daftar Ajuan PIP', icon: FileText, testid: 'nav-admin-pip-ajuan' },
          { to: '/admin/alumni', label: 'Data Alumni', icon: GraduationCap, testid: 'nav-admin-alumni' },
          { to: '/admin/naik-kelas', label: 'Naik Kelas & Kelulusan', icon: ArrowRightLeft, testid: 'nav-admin-promotions' },
        ],
      },
      {
        title: 'Akademik',
        items: [
          { to: '/admin/schedules', label: 'Jadwal Pelajaran', icon: Calendar, testid: 'nav-schedules' },
          { to: '/admin/jadwal-piket', label: 'Jadwal Piket', icon: ShieldAlert, testid: 'nav-piket-admin' },
          { to: '/guru-pengganti', label: 'Guru Pengganti', icon: UserRoundCog, testid: 'nav-admin-guru-pengganti' },
          { to: '/admin/jurnal', label: 'Data Jurnal', icon: ClipboardList, testid: 'nav-admin-jurnal' },
          { to: '/admin/indikator-materi', label: 'Data Indikator & Materi', icon: BookOpen, testid: 'nav-admin-indikator-materi' },
          { to: '/admin/materi', label: 'Materi Mapel', icon: BookOpen, testid: 'nav-admin-materi' },
          { to: '/admin/tugas', label: 'Tugas Mapel', icon: ClipboardList, testid: 'nav-admin-tugas' },
          { to: '/nilai/input', label: 'Input Nilai', icon: ClipboardEdit, testid: 'nav-grades-input-admin' },
          { to: '/rapor', label: 'E-Rapor Digital', icon: FileText, testid: 'nav-rapor-admin' },
        ],
      },
      {
        title: 'Aktivitas & Program',
        items: [
          { to: '/admin/kegiatan-madrasah', label: 'Kegiatan Madrasah', icon: CalendarDays, testid: 'nav-admin-kegiatan-madrasah' },
          { to: '/admin/dana-rkam', label: 'DANA RKAM', icon: DollarSign, testid: 'nav-admin-dana-rkam' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-admin' },
          { to: '/ekstrakurikuler', label: 'Ekstrakurikuler', icon: Sparkles, testid: 'nav-ekstra-admin' },
          { to: '/admin/kebersihan', label: 'Rekapitulasi Kebersihan', icon: Sparkles, testid: 'nav-admin-kebersihan' },
          { to: '/admin/laporan', label: 'Data Laporan', icon: FileText, testid: 'nav-admin-laporan' },
          { to: '/piket/tugas', label: 'Tugas & Piket', icon: ListChecks, testid: 'nav-admin-piket-tasks' },
          { to: '/admin/pengumuman', label: 'Pengumuman', icon: Megaphone, testid: 'nav-admin-announcements' },
          { to: '/admin/aplikasi-madrasah', label: 'Aplikasi Madrasah', icon: Globe, testid: 'nav-admin-apps' },
        ],
      },
      {
        title: 'Manajemen Tatib & BK',
        items: [
          ...menuTatib('admin', 'nav-admin'),
        ],
      },
      {
        title: 'Menu BK',
        items: [
          { to: '/admin/bk/kunjungan', label: 'Kunjungan Konseling', icon: HeartHandshake, testid: 'nav-admin-bk-kunjungan' },
          { to: '/admin/bk/clkb', label: 'Cek List Kebiasaan Belajar (CLKB)', icon: ClipboardCheck, testid: 'nav-admin-bk-clkb' },
          { to: '/admin/bk/pcl', label: 'Problem Cek List (PCL)', icon: AlertTriangle, testid: 'nav-admin-bk-pcl' },
          { to: '/admin/bk/home-visit', label: 'Jurnal Home Visit', icon: Home, testid: 'nav-admin-bk-home-visit' },
          { to: '/admin/alumni', label: 'Data Alumni', icon: GraduationCap, testid: 'nav-admin-bk-alumni' },
          { to: '/admin/bk/sekolah-lanjutan', label: 'Data Sekolah Lanjutan', icon: School, testid: 'nav-admin-bk-sekolah-lanjutan' },
          { to: '/admin/bk/laporan', label: 'Laporan BK', icon: FileBarChart, testid: 'nav-admin-bk-laporan' },
        ],
      },
      {
        title: 'Menu Perpus',
        items: [
          { to: '/admin/perpus/peminjaman', label: 'Data Peminjaman Buku', icon: BookOpen, testid: 'nav-admin-perpus-peminjaman' },
          { to: '/admin/perpus/kunjungan', label: 'Data Kunjungan Perpus', icon: Users, testid: 'nav-admin-perpus-kunjungan' },
          { to: '/admin/perpus/koleksi', label: 'Data Aset & Koleksi Perpus', icon: BookMarked, testid: 'nav-admin-perpus-koleksi' },
          { to: '/admin/perpus/laporan', label: 'Laporan Perpus', icon: FileBarChart, testid: 'nav-admin-perpus-laporan' },
        ],
      },
      {
        title: 'Menu UKS',
        items: [
          { to: '/admin/uks/kunjungan', label: 'Data Kunjungan', icon: Stethoscope, testid: 'nav-admin-uks-kunjungan' },
          { to: '/admin/uks/ckg', label: 'Data CKG', icon: Syringe, testid: 'nav-admin-uks-ckg' },
          { to: '/admin/uks/obat', label: 'Data Obat', icon: Pill, testid: 'nav-admin-uks-obat' },
          { to: '/admin/uks/bmhp', label: 'Data BMHP', icon: Package, testid: 'nav-admin-uks-bmhp' },
          { to: '/admin/uks/jenis-penanganan', label: 'Jenis Penanganan', icon: ClipboardPlus, testid: 'nav-admin-uks-jenis-penanganan' },
          { to: '/admin/uks/penegakan-diagnosa', label: 'Penegakan Diagnosa', icon: ClipboardCheck, testid: 'nav-admin-uks-diagnosa' },
          { to: '/admin/uks/data-siswa-gtk', label: 'Data Siswa dan GTK', icon: Users, testid: 'nav-admin-uks-data-siswa-gtk' },
          { to: '/admin/uks/aset', label: 'Aset UKS', icon: HeartPulse, testid: 'nav-admin-uks-aset' },
          { to: '/admin/uks/laporan', label: 'Laporan UKS', icon: FileBarChart, testid: 'nav-admin-uks-laporan' },
          { to: '/admin/uks/laporan-baru', label: 'Laporan UKS Baru', icon: FileBarChart, testid: 'nav-admin-uks-laporan-baru' },
        ],
      },
      {
        title: 'Menu Sarpras',
        items: [
          { to: '/admin/sarpras/aset-tetap', label: 'Data Aset Tetap Komite & BMN', icon: Building2, testid: 'nav-admin-sarpras-aset-tetap' },
          { to: '/admin/sarpras/aset-lancar', label: 'Aset Lancar', icon: Package, testid: 'nav-admin-sarpras-aset-lancar' },
          { to: '/admin/sarpras/ruangan-aset', label: 'Data Ruangan dan Aset', icon: DoorOpen, testid: 'nav-admin-sarpras-ruangan-aset' },
          { to: '/admin/sarpras/penghapusan', label: 'Penghapusan Barang', icon: Trash, testid: 'nav-admin-sarpras-penghapusan' },
          { to: '/admin/sarpras/peminjaman-barang', label: 'Peminjaman Barang', icon: Handshake, testid: 'nav-admin-sarpras-peminjaman-barang' },
          { to: '/admin/sarpras/peminjaman-ruangan', label: 'Peminjaman Ruangan', icon: CalendarClock, testid: 'nav-admin-sarpras-peminjaman-ruangan' },
          { to: '/admin/sarpras/jurnal-ruangan', label: 'Jurnal Penggunaan Ruangan', icon: BookOpenCheck, testid: 'nav-admin-sarpras-jurnal-ruangan' },
          { to: '/admin/sarpras/jurnal-alat', label: 'Jurnal Penggunaan Alat', icon: Wrench, testid: 'nav-admin-sarpras-jurnal-alat' },
          { to: '/admin/sarpras/jurnal-perawatan', label: 'Jurnal Perawatan Aset', icon: ClipboardList, testid: 'nav-admin-sarpras-jurnal-perawatan' },
          { to: '/admin/sarpras/kerusakan', label: 'Laporan Kerusakan & Perbaikan', icon: AlertOctagon, testid: 'nav-admin-sarpras-kerusakan' },
        ],
      },
      {
        title: 'Sistem & Utilitas',
        items: [
          { to: '/admin/app-info', label: 'Info & Update', icon: Info, testid: 'nav-admin-app-info' },
          { to: '/admin/import', label: 'Import Excel', icon: FileUp, testid: 'nav-admin-import' },
          { to: '/admin/master-wilayah', label: 'Master Wilayah', icon: MapPin, testid: 'nav-admin-master-wilayah' },
          { to: '/admin/pencocokan-wilayah', label: 'Pencocokan Wilayah', icon: MapPinned, testid: 'nav-admin-pencocokan-wilayah' },
          { to: '/admin/backup', label: 'Backup & Restore', icon: Database, testid: 'nav-admin-backup' },
          { to: '/admin/audit-logs', label: 'Log Aktivitas', icon: ShieldCheck, testid: 'nav-audit' },
          { to: '/admin/settings', label: 'Pengaturan', icon: Settings, testid: 'nav-settings' },
        ],
      },
    ];
  } else if (role === 'waka_kurikulum') {
    // Waka Kurikulum uses grouped menu structure with dedicated routes
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      {
        title: 'Manajemen Siswa',
        items: [
          { to: '/wakakur/siswa', label: 'Data Siswa', icon: GraduationCap, testid: 'nav-wakakur-siswa' },
          { to: '/wakakur/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-wakakur-kehadiran' },
          { to: '/wakakur/buku-induk', label: 'Buku Induk Siswa', icon: FileText, testid: 'nav-wakakur-buku-induk' },
          { to: '/wakakur/cetak-absensi', label: 'Cetak Format Absensi Siswa Manual', icon: FileText, testid: 'nav-wakakur-cetak-absensi' },
          { to: '/wakakur/alumni', label: 'Data Alumni', icon: GraduationCap, testid: 'nav-wakakur-alumni' },
          { to: '/wakakur/mutasi', label: 'Data Mutasi', icon: ArrowRightLeft, testid: 'nav-wakakur-mutasi' },
          { to: '/wakakur/naik-kelas', label: 'Naik Kelas & Kelulusan', icon: ArrowRightLeft, testid: 'nav-wakakur-naik-kelas' },
        ],
      },
      {
        title: 'Akademik',
        items: [
          { to: '/wakakur/jadwal', label: 'Jadwal Pelajaran', icon: Calendar, testid: 'nav-wakakur-schedules' },
          { to: '/wakakur/jadwal-piket', label: 'Jadwal Piket', icon: ShieldAlert, testid: 'nav-wakakur-jadwal-piket' },
          { to: '/guru-pengganti', label: 'Guru Pengganti', icon: UserRoundCog, testid: 'nav-wakakur-guru-pengganti' },
          { to: '/wakakur/jurnal', label: 'Data Jurnal', icon: ClipboardList, testid: 'nav-wakakur-jurnal' },
          { to: '/wakakur/indikator-materi', label: 'Data Indikator & Materi', icon: BookOpen, testid: 'nav-wakakur-indikator-materi' },
          { to: '/wakakur/materi', label: 'Materi Mapel', icon: BookOpen, testid: 'nav-wakakur-materi' },
          { to: '/wakakur/tugas', label: 'Tugas Mapel', icon: ClipboardList, testid: 'nav-wakakur-tugas' },
          { to: '/wakakur/nilai', label: 'Input Nilai', icon: ClipboardEdit, testid: 'nav-wakakur-grades-input' },
          { to: '/wakakur/rapor', label: 'E-Rapor Digital', icon: FileText, testid: 'nav-wakakur-rapor' },
        ],
      },
      {
        title: 'Aktivitas & Program',
        items: [
          { to: '/wakakur/kegiatan', label: 'Kegiatan Madrasah', icon: CalendarDays, testid: 'nav-wakakur-kegiatan' },
          { to: '/wakakur/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-wakakur-prestasi' },
          { to: '/wakakur/piket', label: 'Tugas & Piket', icon: ListChecks, testid: 'nav-wakakur-piket-tasks' },
          { to: '/wakakur/pengumuman', label: 'Pengumuman', icon: Megaphone, testid: 'nav-wakakur-pengumuman' },
          { to: '/admin/dana-rkam', label: 'DANA RKAM', icon: DollarSign, testid: 'nav-wakakur-rkam' },
        ],
      },
      {
        title: 'Tata Tertib',
        items: menuTatib('waka_kurikulum', 'nav-wakakur'),
      },
    ];
  } else if (role === 'waka_sarpras') {
    // Waka Sarana Prasarana: grup Sarpras yang sama seperti admin (view + kelola aset/ruangan/peminjaman).
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/profile/guru', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-waka-sarpras' },
      { to: '/my-agenda', label: 'Agenda Saya', icon: CalendarDays, testid: 'nav-waka-sarpras-agenda' },
      {
        title: 'Menu Sarpras',
        items: [
          { to: '/admin/sarpras/aset-tetap', label: 'Data Aset Tetap Komite & BMN', icon: Building2, testid: 'nav-waka-sarpras-aset-tetap' },
          { to: '/admin/sarpras/aset-lancar', label: 'Aset Lancar', icon: Package, testid: 'nav-waka-sarpras-aset-lancar' },
          { to: '/admin/sarpras/ruangan-aset', label: 'Data Ruangan dan Aset', icon: DoorOpen, testid: 'nav-waka-sarpras-ruangan-aset' },
          { to: '/admin/sarpras/penghapusan', label: 'Penghapusan Barang', icon: Trash, testid: 'nav-waka-sarpras-penghapusan' },
          { to: '/admin/sarpras/peminjaman-barang', label: 'Peminjaman Barang', icon: Handshake, testid: 'nav-waka-sarpras-peminjaman-barang' },
          { to: '/admin/sarpras/peminjaman-ruangan', label: 'Peminjaman Ruangan', icon: CalendarClock, testid: 'nav-waka-sarpras-peminjaman-ruangan' },
          { to: '/admin/sarpras/jurnal-ruangan', label: 'Jurnal Penggunaan Ruangan', icon: BookOpenCheck, testid: 'nav-waka-sarpras-jurnal-ruangan' },
          { to: '/admin/sarpras/jurnal-alat', label: 'Jurnal Penggunaan Alat', icon: Wrench, testid: 'nav-waka-sarpras-jurnal-alat' },
          { to: '/admin/sarpras/jurnal-perawatan', label: 'Jurnal Perawatan Aset', icon: ClipboardList, testid: 'nav-waka-sarpras-jurnal-perawatan' },
          { to: '/admin/sarpras/kerusakan', label: 'Laporan Kerusakan & Perbaikan', icon: AlertOctagon, testid: 'nav-waka-sarpras-kerusakan' },
        ],
      },
      {
        title: 'Aktivitas & Program',
        items: [
          { to: '/admin/kegiatan-madrasah', label: 'Kegiatan Madrasah', icon: CalendarDays, testid: 'nav-waka-sarpras-kegiatan' },
          { to: '/admin/dana-rkam', label: 'DANA RKAM', icon: DollarSign, testid: 'nav-waka-sarpras-rkam' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-waka-sarpras' },
        ],
      },
    ];
  } else if (role === 'waka_kesiswaan') {
    // Waka Kesiswaan: grup Manajemen Siswa, Aktivitas & Program, dan Tatib & BK (boleh mencatat poin tatib; BK view-only).
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/profile/guru', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-waka-kesiswaan' },
      { to: '/my-agenda', label: 'Agenda Saya', icon: CalendarDays, testid: 'nav-waka-kesiswaan-agenda' },
      {
        title: 'Manajemen Siswa',
        items: [
          { to: '/admin/siswa', label: 'Data Siswa', icon: GraduationCap, testid: 'nav-waka-kesiswaan-siswa' },
          { to: '/admin/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-waka-kesiswaan-kehadiran' },
          { to: '/admin/mutasi', label: 'Data Mutasi', icon: ArrowRightLeft, testid: 'nav-waka-kesiswaan-mutasi' },
          { to: '/admin/alumni', label: 'Data Alumni', icon: GraduationCap, testid: 'nav-waka-kesiswaan-alumni' },
        ],
      },
      {
        title: 'Aktivitas & Program',
        items: [
          { to: '/admin/kegiatan-madrasah', label: 'Kegiatan Madrasah', icon: CalendarDays, testid: 'nav-waka-kesiswaan-kegiatan' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-waka-kesiswaan' },
          { to: '/ekstrakurikuler', label: 'Ekstrakurikuler', icon: Sparkles, testid: 'nav-waka-kesiswaan-ekstra' },
          { to: '/admin/kebersihan', label: 'Rekapitulasi Kebersihan', icon: Sparkles, testid: 'nav-waka-kesiswaan-kebersihan' },
          { to: '/admin/pengumuman', label: 'Pengumuman', icon: Megaphone, testid: 'nav-waka-kesiswaan-pengumuman' },
          { to: '/admin/dana-rkam', label: 'DANA RKAM', icon: DollarSign, testid: 'nav-waka-kesiswaan-rkam' },
        ],
      },
      {
        title: 'Tatib & BK',
        items: [
          ...menuTatib('waka_kesiswaan', 'nav-waka-kesiswaan'),
          { to: '/admin/bk/kunjungan', label: 'Kunjungan Konseling', icon: HeartHandshake, testid: 'nav-waka-kesiswaan-bk-kunjungan' },
          { to: '/admin/bk/laporan', label: 'Laporan BK', icon: FileBarChart, testid: 'nav-waka-kesiswaan-bk-laporan' },
          { to: '/admin/bk/sekolah-lanjutan', label: 'Data Sekolah Lanjutan', icon: School, testid: 'nav-waka-kesiswaan-sekolah-lanjutan' },
        ],
      },
    ];
  } else if (role === 'penjamin_mutu') {
    // Penjamin Mutu uses grouped menu structure, mirroring admin's groups (view-only oversight).
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/profile/guru', label: 'Profil Saya', icon: UserCircle, testid: 'nav-profile-mutu' },
      { to: '/my-agenda', label: 'Agenda Saya', icon: CalendarDays, testid: 'nav-mutu-agenda' },
      {
        title: 'Manajemen GTK',
        items: [
          { to: '/admin/gtk', label: 'Data GTK', icon: Briefcase, testid: 'nav-mutu-gtk' },
          { to: '/admin/buku-induk-kepegawaian', label: 'Buku Induk Kepegawaian', icon: BookMarked, testid: 'nav-mutu-buku-induk-kepegawaian' },
          { to: '/admin/gtk/laporan-absensi', label: 'Laporan Absensi', icon: UserCheck, testid: 'nav-mutu-gtk-absensi' },
          { to: '/admin/gtk/agenda-guru', label: 'Agenda Guru', icon: Calendar, testid: 'nav-mutu-gtk-agenda-guru' },
          { to: '/admin/gtk/agenda-tendik', label: 'Agenda Tendik', icon: Calendar, testid: 'nav-mutu-gtk-agenda-tendik' },
          { to: '/admin/gtk/e-kinerja', label: 'E-Kinerja', icon: Target, testid: 'nav-mutu-gtk-ekinerja' },
          { to: '/admin/gtk/profesionalitas', label: 'Profesionalitas GTK', icon: Award, testid: 'nav-mutu-gtk-profesionalitas' },
        ],
      },
      {
        title: 'Manajemen Siswa',
        items: [
          { to: '/admin/siswa', label: 'Data Siswa', icon: GraduationCap, testid: 'nav-mutu-siswa' },
          { to: '/admin/kehadiran', label: 'Kehadiran Siswa', icon: UserCheck, testid: 'nav-mutu-kehadiran' },
          { to: '/admin/buku-induk', label: 'Buku Induk Siswa', icon: FileText, testid: 'nav-mutu-buku-induk' },
          { to: '/admin/cetak-absensi-manual', label: 'Cetak Format Absensi Siswa Manual', icon: FileText, testid: 'nav-mutu-cetak-absensi-manual' },
          { to: '/admin/alumni', label: 'Data Alumni', icon: GraduationCap, testid: 'nav-mutu-alumni' },
          { to: '/admin/naik-kelas', label: 'Naik Kelas & Kelulusan', icon: ArrowRightLeft, testid: 'nav-mutu-promotions' },
        ],
      },
      {
        title: 'Akademik',
        items: [
          { to: '/admin/schedules', label: 'Jadwal Pelajaran', icon: Calendar, testid: 'nav-mutu-schedules' },
          { to: '/admin/jadwal-piket', label: 'Jadwal Piket', icon: ShieldAlert, testid: 'nav-mutu-piket' },
          { to: '/admin/jurnal', label: 'Data Jurnal', icon: ClipboardList, testid: 'nav-mutu-jurnal' },
          { to: '/admin/indikator-materi', label: 'Data Indikator & Materi', icon: BookOpen, testid: 'nav-mutu-indikator-materi' },
          { to: '/admin/materi', label: 'Materi Mapel', icon: BookOpen, testid: 'nav-mutu-materi' },
          { to: '/admin/tugas', label: 'Tugas Mapel', icon: ClipboardList, testid: 'nav-mutu-tugas' },
          { to: '/rapor', label: 'E-Rapor Digital', icon: FileText, testid: 'nav-mutu-rapor' },
        ],
      },
      {
        title: 'Aktivitas & Program',
        items: [
          { to: '/admin/kegiatan-madrasah', label: 'Kegiatan Madrasah', icon: CalendarDays, testid: 'nav-mutu-kegiatan' },
          { to: '/admin/dana-rkam', label: 'DANA RKAM', icon: DollarSign, testid: 'nav-mutu-rkam' },
          { to: '/prestasi', label: 'Data Prestasi', icon: Trophy, testid: 'nav-prestasi-mutu' },
          { to: '/ekstrakurikuler', label: 'Ekstrakurikuler', icon: Sparkles, testid: 'nav-mutu-ekstra' },
          { to: '/admin/kebersihan', label: 'Rekapitulasi Kebersihan', icon: Sparkles, testid: 'nav-mutu-kebersihan' },
          { to: '/admin/pengumuman', label: 'Pengumuman', icon: Megaphone, testid: 'nav-mutu-pengumuman' },
        ],
      },
      {
        title: 'Manajemen Tatib & BK',
        items: [
          ...menuTatib('penjamin_mutu', 'nav-mutu'),
          { to: '/admin/bk/kunjungan', label: 'Kunjungan Konseling', icon: HeartHandshake, testid: 'nav-mutu-bk-kunjungan' },
          { to: '/admin/bk/laporan', label: 'Laporan BK', icon: FileBarChart, testid: 'nav-mutu-bk-laporan' },
          { to: '/admin/bk/sekolah-lanjutan', label: 'Data Sekolah Lanjutan', icon: School, testid: 'nav-mutu-sekolah-lanjutan' },
        ],
      },
    ];
  }
  return items;
}

// Menu per peran + menu lintas peran (Simpan Akun, Masterplan, Reset PIN) dari lib/menuUmum.js.
function navForRole(role, userRoles = []) {
  const items = tambahMenuUmum(navForRoleDasar(role, userRoles), role);
  return items;
}

function ActivePeriodCard({ ctx, onClick }) {
  if (!ctx || !ctx.year_name) {
    return (
      <div
        className="mx-3 mt-3 mb-1 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
        role="status"
        aria-live="polite"
      >
        Belum ada Tahun Pelajaran aktif. Buat di menu Tahun Pelajaran.
      </div>
    );
  }
  // Show orange override UI only if user has override AND it's different from active global
  const showOverrideUI = ctx.is_override && !ctx.isViewingSameSemesterAsActive;

  const ariaLabel = showOverrideUI
    ? `Mode Lihat - Tahun Pelajaran ${ctx.year_name}, Semester ${ctx.semester_name}. Klik untuk ubah atau reset periode.`
    : `Periode Aktif - Tahun Pelajaran ${ctx.year_name}, Semester ${ctx.semester_name}. Klik untuk pindah ke tahun pelajaran lampau.`;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="sidebar-active-period"
      aria-label={ariaLabel}
      aria-expanded="false"
      className={`group mx-3 mt-3 mb-1 rounded-xl p-3 text-white shadow-sm w-[calc(100%-1.5rem)] text-left transition-all hover:shadow-md focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#006837] ${
        showOverrideUI
          ? 'bg-gradient-to-br from-amber-600 to-amber-700 ring-2 ring-amber-300/50'
          : 'bg-gradient-to-br from-[#006837] to-[#0B7A3B]'
      }`}
    >
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 opacity-80" />
          <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
            {showOverrideUI ? 'Mode Lihat' : 'Periode Aktif'}
          </div>
        </div>
        <ChevronDown className="h-3 w-3 opacity-70 group-hover:opacity-100" />
      </div>

      {/* Tahun Takwim - tampilkan dari semester yang dipilih */}
      {ctx.tahun_takwim_info && ctx.tahun_takwim_info.length > 0 && (() => {
        // Ambil Tahun Takwim pertama dari semester yang dipilih (bukan is_active)
        const currentTT = ctx.tahun_takwim_info[0];
        if (!currentTT) return null;
        return (
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider opacity-70">Tahun Takwim</span>
            <Badge className="bg-white/20 text-white border-white/30 text-[10px] font-bold px-2 py-0">
              {currentTT.year}
            </Badge>
          </div>
        );
      })()}

      {/* Tahun Pelajaran */}
      <div className="mt-1.5 pt-1.5 border-t border-white/20 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-80">
          <GraduationCap className="h-3 w-3 inline mr-1 opacity-80" />
          Tahun Pelajaran
        </span>
      </div>
      <div className="font-mono text-base font-extrabold tabular-nums tracking-tight" data-testid="sidebar-tp-name">{ctx.year_name}</div>

      {/* Semester */}
      <div className="mt-2 pt-2 border-t border-white/20 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-80">Semester</span>
        <Badge data-testid="sidebar-semester" className="bg-amber-300/95 text-amber-900 border-0 capitalize text-[10px] font-bold px-2 py-0">
          {ctx.semester_name || '-'}
        </Badge>
      </div>

      {/* Kurikulum */}
      <div className="mt-1 flex items-center justify-between gap-1">
        <span className="text-[10px] uppercase tracking-wider opacity-80 shrink-0">Kurikulum</span>
        {ctx.curriculum_name ? (
          <Badge data-testid="sidebar-curriculum" className="bg-white/20 text-white border-white/30 text-[10px] font-semibold px-2 py-0 truncate max-w-[120px]" title={ctx.curriculum_name}>
            {ctx.curriculum_name}
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-rose-100/90 text-rose-800 border-rose-200 text-[10px] font-semibold px-2 py-0" data-testid="sidebar-curriculum-empty">
            Belum diset
          </Badge>
        )}
      </div>

      {showOverrideUI && (
        <div className="text-[9px] mt-1 italic opacity-90">Klik untuk ubah / reset</div>
      )}
      {!showOverrideUI && (
        <div className="text-[9px] mt-1 italic opacity-70">Klik untuk pindah TP lampau</div>
      )}
    </button>
  );
}

function Sidebar({ items, current, onItemClick, viewCtx, onTPClick }) {
  // Check if items is grouped (has title property) or flat
  const isGrouped = items.some((item) => item.title);

  // State to track which groups are expanded (default all expanded)
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const groups = {};
    items.forEach((item, idx) => {
      if (item.title) {
        groups[item.title || idx] = true;
      }
    });
    return groups;
  });

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !(prev[groupKey] ?? true),
    }));
  };

  const renderMenuItem = (it) => {
    const Icon = it.icon;
    const active = current === it.to;
    return (
      <Link
        key={it.to}
        to={it.to}
        data-testid={it.testid}
        onClick={onItemClick}
        aria-label={it.label}
        aria-current={active ? 'page' : undefined}
        className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-[#006837] focus-visible:ring-offset-2 ${
          active
            ? 'bg-[#006837] text-white shadow-sm'
            : 'text-slate-700 hover:bg-[#006837]/8 hover:text-[#006837]'
        } ${it.highlight && !active ? 'bg-amber-50 text-amber-900 border border-amber-200' : ''}`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span className="flex-1">{it.label}</span>
        {active && <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
      </Link>
    );
  };

  return (
    <div className="flex flex-col">
      <ActivePeriodCard ctx={viewCtx} onClick={onTPClick} />
      <nav className="flex flex-col gap-1 px-3 py-3" data-testid="sidebar-nav" aria-label="Menu navigasi utama">
        {isGrouped ? (
          items.map((item, idx) => {
            // If item has 'to' property, it's a regular menu item
            if (item.to) {
              return renderMenuItem(item);
            }
            // Otherwise it's a group
            const groupKey = item.title || idx;
            // Grup yang muncul belakangan (mis. menu PKG yang dimuat async) ikut terbuka seperti grup lain.
            const isExpanded = expandedGroups[groupKey] ?? true;
            return (
              <div key={groupKey} className="mt-3 first:mt-0">
                <button
                  onClick={() => toggleGroup(groupKey)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-[#006837] transition-colors rounded-lg hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-[#006837] focus-visible:ring-offset-2"
                  data-testid={`group-toggle-${groupKey}`}
                  aria-expanded={isExpanded}
                  aria-controls={`group-content-${groupKey}`}
                  aria-label={`${isExpanded ? 'Tutup' : 'Buka'} grup menu ${item.title}`}
                >
                  <span>{item.title}</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? '' : '-rotate-90'}`} aria-hidden="true" />
                </button>
                {isExpanded && (
                  <div
                    className="flex flex-col gap-1 mt-1"
                    id={`group-content-${groupKey}`}
                    role="group"
                    aria-label={`Menu ${item.title}`}
                  >
                    {item.items.map((it) => renderMenuItem(it))}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          items.map((it) => renderMenuItem(it))
        )}
      </nav>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, activeRole, logout, switchRole, stopImpersonate, settings, refreshMe } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [viewCtx, setViewCtx] = useState(null);
  const [vcDialogOpen, setVcDialogOpen] = useState(false);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const [stoppingImpersonate, setStoppingImpersonate] = useState(false);

  // Hak membuka menu Guru Pengganti ditentukan server (GET /guru-pengganti/config);
  // sebelum respons tiba / bila gagal, pakai aturan lokal yang sama (admin, waka kurikulum, guru piket).
  const [serverCanManageGP, setServerCanManageGP] = useState(null);
  useEffect(() => {
    let alive = true;
    setServerCanManageGP(null);
    if (!activeRole) return undefined;
    api.get('/guru-pengganti/config')
      .then(({ data }) => { if (alive) setServerCanManageGP(!!data?.can_manage); })
      .catch(() => {});
    return () => { alive = false; };
  }, [activeRole]);

  // Grup menu PKG bergantung pada keanggotaan SK aktif (diperiksa server), dimuat sekali per pengguna/peran.
  const [itemPkg, setItemPkg] = useState([]);
  useEffect(() => {
    let alive = true;
    setItemPkg([]);
    if (!perluMenuPkg(activeRole)) return undefined;
    (async () => {
      try {
        const k = await ambilKeanggotaan();
        const menu = k?.anggota ? await ambilMenuPkg() : [];
        if (alive) setItemPkg(menuPkg(k, menu));
      } catch { /* menu PKG disembunyikan bila gagal */ }
    })();
    return () => { alive = false; };
  }, [activeRole, user?.id]);

  // Get base menu items for role
  const roleItems = tambahMenuPkg(navForRole(activeRole, user?.roles || []), itemPkg);
  const showGuruPengganti = serverCanManageGP ?? canManageGuruPengganti(activeRole);
  const items = showGuruPengganti ? roleItems : withoutGuruPengganti(roleItems);

  // Note: Public pages menu items are NOT added to sidebar
  // They appear in the dashboard content via DashboardRouter/individual dashboards

  const handleStopImpersonate = async () => {
    if (stoppingImpersonate) return;
    setStoppingImpersonate(true);
    try {
      await stopImpersonate();
      toast.success('Kembali ke akun admin');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal kembali ke akun admin');
      setStoppingImpersonate(false);
    }
  };

  // Auto-prompt password change on first load if needed (skip for 'kelas' role).
  useEffect(() => {
    const ps = user?.password_status;
    const activeRole = user?.active_role || (user?.roles?.[0]);
    // Don't prompt password change for 'kelas' role
    if (ps?.should_prompt && activeRole !== 'kelas') {
      const alreadyShown = sessionStorage.getItem('matsa_pw_prompt_shown');
      if (!alreadyShown) {
        setPwDialogOpen(true);
        sessionStorage.setItem('matsa_pw_prompt_shown', '1');
      }
    }
  }, [user?.id, user?.active_role, user?.roles]);

  const refreshViewCtx = React.useCallback(() => {
    api.get('/auth/view-context').then(({ data }) => {
      // Enhance data with logic to detect if override is same as active
      const activeSemester = (data.available_semesters || []).find(s => s.is_active);
      const isViewingSameSemesterAsActive = data.semester_id && activeSemester && data.semester_id === activeSemester.id;
      data.isViewingSameSemesterAsActive = isViewingSameSemesterAsActive;
      setViewCtx(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshViewCtx();
  }, [activeRole, refreshViewCtx]);

  // Load app version
  useEffect(() => {
    api.get('/app-info')
      .then(({ data }) => setAppVersion(data.current_version))
      .catch(() => setAppVersion('1.0.0')); // fallback
  }, []);

  const handleLogout = async () => {
    await logout();
    nav('/login');
  };

  const handleSwitchRole = async (r) => {
    try {
      await switchRole(r);
      toast.success(`Berganti ke peran: ${ROLE_LABELS[r] || r}`);
      nav('/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal berganti peran');
    }
  };

  // Mode tertanam (aplikasi Android): beri tahu aplikasi rute & judul halaman yang sedang dibuka.
  const embedded = isEmbeddedApp();
  useEffect(() => {
    if (!embedded) return;
    postToApp({ type: 'route', path: loc.pathname + loc.search, title: document.title });
  }, [embedded, loc.pathname, loc.search]);

  const userInitial = (user?.full_name || user?.username || 'U').substring(0, 2).toUpperCase();
  const appName = settings?.app_name || 'Super Apps MATSANDATAMA';
  const schoolName = settings?.school_name || 'MTsN 2 Kota Malang';

  if (embedded) {
    // Di dalam aplikasi: hanya isi halaman. Navigasi, peran, notifikasi & keluar ditangani aplikasi.
    return (
      <div className="min-h-screen bg-[var(--cream)]" data-testid="app-shell-embedded">
        <main className="px-3 sm:px-6 py-4 max-w-7xl mx-auto w-full">{children}</main>
        <ViewContextDialog
          open={vcDialogOpen}
          onOpenChange={setVcDialogOpen}
          onUpdated={() => { refreshViewCtx(); refreshMe?.(); }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--cream)] flex">
      <EnableNotifications />
      <CommandPalette />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-slate-200 sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="h-10 w-10 object-contain" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-[#006837] flex items-center justify-center text-white font-bold">MS</div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-bold text-[#006837] truncate">SUPER APPS</div>
              <div className="text-xs text-slate-600 truncate">MATSANDATAMA</div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Sidebar items={items} current={loc.pathname} viewCtx={viewCtx} onTPClick={() => setVcDialogOpen(true)} />
        </div>
        <div className="p-3 border-t border-slate-100 space-y-2">
          <button
            onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-600 hover:text-[#006837] hover:bg-slate-50 rounded-lg transition-colors group"
            aria-label="Buka Command Palette"
          >
            <span className="font-medium">Pencarian Cepat</span>
            <kbd className="inline-flex h-5 select-none items-center gap-1 rounded border bg-slate-100 px-1.5 font-mono text-[10px] font-medium text-slate-600 group-hover:bg-slate-200">
              <span className="text-xs">⌘</span>K
            </kbd>
          </button>
          <div className="text-xs text-slate-600 font-medium">{schoolName}</div>
          {appVersion && (
            <div className="text-[10px] text-slate-400">Version {appVersion}</div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
          <div className="flex items-center justify-between px-4 lg:px-6 h-16">
            <div className="flex items-center gap-3 min-w-0">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0 flex flex-col">
                  <div className="px-5 py-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      {settings?.logo_url ? (
                        <img src={settings.logo_url} alt="Logo" className="h-10 w-10 object-contain" />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-[#006837] flex items-center justify-center text-white font-bold">MS</div>
                      )}
                      <div>
                        <div className="text-sm font-bold text-[#006837]">SUPER APPS</div>
                        <div className="text-xs text-slate-600">MATSANDATAMA</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <Sidebar items={items} current={loc.pathname} onItemClick={() => setMobileOpen(false)} viewCtx={viewCtx} onTPClick={() => { setVcDialogOpen(true); setMobileOpen(false); }} />
                  </div>
                  <div className="p-3 border-t border-slate-100">
                    <div className="text-xs text-slate-600 font-medium">{schoolName}</div>
                    {appVersion && (
                      <div className="text-[10px] text-slate-400 mt-1">Version {appVersion}</div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
              <div className="min-w-0">
                <div className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                  {appName}
                </div>
                <div className="text-xs text-slate-500 hidden sm:block">{schoolName}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Panduan button */}
              <Button
                variant="ghost"
                onClick={() => nav('/panduan')}
                className="h-11 w-11 sm:h-10 sm:w-10 rounded-full hover:bg-[#006837]/8 hidden sm:inline-flex focus-visible:ring-2 focus-visible:ring-[#006837] focus-visible:ring-offset-2 p-0 items-center justify-center"
                data-testid="topbar-panduan-btn"
                aria-label="Panduan Pengguna"
              >
                <HelpCircle className="h-5 w-5 text-slate-700" aria-hidden="true" />
              </Button>

              {/* Notification Bell */}
              <NotificationBell />

              {/* Offline Queue Badge - show for roles that can submit journals */}
              {['guru', 'admin'].includes(activeRole) && <OfflineQueueBadge />}

              {/* Back to Admin button - show only when impersonating */}
              {user?.is_impersonating && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStopImpersonate}
                  disabled={stoppingImpersonate}
                  className="gap-2 bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                  data-testid="stop-impersonate-btn"
                  title={`Kembali ke akun ${user.impersonator_username}`}
                >
                  {stoppingImpersonate ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogIn className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Kembali ke Admin</span>
                </Button>
              )}

              {/* Role switcher next to avatar - ALWAYS visible */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="role-switcher-trigger" className="gap-2 h-9 hidden sm:flex">
                    <Badge variant="secondary" className="bg-[#006837]/10 text-[#006837] border-0 px-1.5 py-0">
                      {ROLE_LABELS[activeRole] || activeRole}
                    </Badge>
                    <ChevronRight className="h-3.5 w-3.5 rotate-90 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    {user?.roles?.length > 1 ? 'Berganti Peran' : 'Peran Aktif'}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(user?.roles || []).map((r) => (
                    <DropdownMenuItem
                      key={r}
                      data-testid={`role-option-${r}`}
                      onClick={() => r !== activeRole && handleSwitchRole(r)}
                      className={activeRole === r ? 'bg-[#006837]/8 text-[#006837] font-semibold' : ''}
                    >
                      {ROLE_LABELS[r] || r}
                      {activeRole === r && <span className="ml-auto text-xs">aktif</span>}
                    </DropdownMenuItem>
                  ))}
                  {user?.roles?.length === 1 && (
                    <div className="px-2 py-1.5 text-xs text-slate-500">Anda hanya memiliki 1 peran</div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Avatar + profile menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="profile-menu-trigger" className="rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-[#006837] text-white text-xs font-semibold">{userInitial}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>
                    <div className="font-semibold">{user?.full_name}</div>
                    <div className="text-xs text-slate-500 font-normal">@{user?.username}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Mobile-only: show role switcher in menu */}
                  <div className="sm:hidden">
                    {(user?.roles || []).map((r) => (
                      <DropdownMenuItem
                        key={`mobile-${r}`}
                        onClick={() => r !== activeRole && handleSwitchRole(r)}
                        className={activeRole === r ? 'bg-[#006837]/8 text-[#006837]' : ''}
                      >
                        {ROLE_LABELS[r] || r}
                        {activeRole === r && <span className="ml-auto text-xs">aktif</span>}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </div>
                  {/* Hide Profile and Change Password for 'kelas' role */}
                  {activeRole !== 'kelas' && (
                    <>
                      <DropdownMenuItem onClick={() => nav('/profile')} data-testid="menu-profile">
                        <UserCircle className="h-4 w-4 mr-2" /> Profil Saya
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setPwDialogOpen(true); }} data-testid="menu-change-password">
                        <ShieldCheck className="h-4 w-4 mr-2" /> Ubah Password
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={() => nav('/panduan')} data-testid="menu-panduan" className="sm:hidden">
                    <HelpCircle className="h-4 w-4 mr-2" /> Panduan Pengguna
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                    <LogOut className="h-4 w-4 mr-2" /> Keluar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto w-full">
          <motion.div
            key={loc.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            {children}
          </motion.div>
        </main>
      </div>

      {/* Password change suggestion dialog (auto-prompts on first load if needed) */}
      <ChangePasswordDialog
        open={pwDialogOpen}
        onOpenChange={setPwDialogOpen}
        reason={user?.password_status?.reason}
        message={user?.password_status?.message}
        onSuccess={() => refreshMe?.()}
        onDismiss={() => refreshMe?.()}
      />

      {/* View Context dialog (per-user TP/Semester override) */}
      <ViewContextDialog
        open={vcDialogOpen}
        onOpenChange={setVcDialogOpen}
        onUpdated={() => { refreshViewCtx(); refreshMe?.(); }}
      />
    </div>
  );
}
